import Keycloak from 'keycloak-js'

import { User, USER_ROLE } from '@img-arena/consoles-api-streaming'
import { DGE_AUTH_METHOD, PLATFORM } from '@/constants/platform'
import { DGE_ENDPOINTS } from '@/service/constants/dgeEndpoints'
import { fetchData } from '@/service/request'
import { useAuthStore } from '@/stores/useAuthStore'
import { notifyError } from '@/utils/notifyError'

const ALLOWED_ROLES = [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN] as const
const TEMP_USER = { name: 'Loading...', email: 'Loading...' } as const
const TEMP_ROLE = USER_ROLE.ADMIN

interface SessionManagerCallbacks {
  onSessionSetupComplete: () => void
}

/**
 * Manages user session setup, validation, and authentication state
 */
export class SessionManager {
  constructor(private callbacks: SessionManagerCallbacks) {}

  async setupSession(keycloak: Keycloak, shouldCleanupUrl: boolean): Promise<boolean> {
    if (!keycloak.token) {
      return false
    }

    this.setTempAuthData(keycloak.token)

    const userData = await this.fetchAndValidateUser(keycloak)
    if (!userData) {
      return false
    }

    this.setCompleteAuthData(keycloak.token, userData)

    if (shouldCleanupUrl) {
      this.cleanupUrlParams()
    }

    this.callbacks.onSessionSetupComplete()

    return true
  }

  updateToken(token: string): void {
    const authStore = useAuthStore.getState()

    if (authStore.isAuthenticated && authStore.dgeAuthMethod === DGE_AUTH_METHOD.KEYCLOAK) {
      authStore.setAuthData(
        token,
        authStore.userRole || TEMP_ROLE,
        PLATFORM.DGE,
        authStore.user || TEMP_USER,
        DGE_AUTH_METHOD.KEYCLOAK
      )
    }
  }

  clearAuthData(): void {
    useAuthStore.getState().clearAuthData()
  }

  private async fetchAndValidateUser(keycloak: Keycloak): Promise<User | null> {
    let userData: User

    try {
      userData = await fetchData<User>({
        endpointData: DGE_ENDPOINTS.Auth.CurrentUser
      })
    } catch (error) {
      notifyError('Failed to fetch user information. Please try again.')
      this.clearAuthData()
      return null
    }

    if (!this.validateUserRole(userData)) {
      await this.handleAuthorizationFailure(keycloak, userData)
      return null
    }

    return userData
  }

  private validateUserRole(userData: User): boolean {
    return ALLOWED_ROLES.includes(userData.role as (typeof ALLOWED_ROLES)[number])
  }

  private async handleAuthorizationFailure(keycloak: Keycloak, userData: User): Promise<void> {
    notifyError(
      `Access denied for ${userData.email}. You need ${ALLOWED_ROLES.join(' or ')} role to access this application.`
    )

    this.clearAuthData()
    await keycloak.logout({ redirectUri: this.getLogoutRedirectUri() })
  }

  private setTempAuthData(token: string): void {
    useAuthStore
      .getState()
      .setAuthData(token, TEMP_ROLE, PLATFORM.DGE, TEMP_USER, DGE_AUTH_METHOD.KEYCLOAK)
  }

  private setCompleteAuthData(token: string, userData: User): void {
    const user = {
      name: userData.fullName,
      email: userData.email
    }

    useAuthStore
      .getState()
      .setAuthData(token, userData.role, PLATFORM.DGE, user, DGE_AUTH_METHOD.KEYCLOAK)
  }

  private cleanupUrlParams(): void {
    window.history.replaceState({}, document.title, window.location.pathname)
  }

  private getLogoutRedirectUri(): string {
    return `${window.location.origin}/login`
  }
}
